import React, { forwardRef, useMemo } from "react";
import { Box, Chip, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PersonIcon from "@mui/icons-material/Person";
import BuildIcon from "@mui/icons-material/Build";
import CalculateIcon from "@mui/icons-material/Calculate";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs"

const BLUE = "#0b4aa2";
const LIGHT_BLUE = "#e3f0fc";
const BORDER = "#dbe5f0";
const MAX_WIDTH = 800;
const COLOR = "#003C59";
const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 };

const theme = createTheme({
  typography: { fontFamily: "'Segoe UI', Roboto, Arial, sans-serif" },
  palette: { primary: { main: BLUE } },
});

const tableSx = {
  "& .MuiTableCell-root": { fontSize: 11, lineHeight: 1.35, py: 0.5, px: 1, borderColor: BORDER },
};
const headCell = { fontWeight: 700, bgcolor: "#eef2f7", color: "#1c2b3a" };
const tableBox = { border: `1px solid ${BORDER}`, borderRadius: 1, bgcolor: "#fff" };

function Section({ icon, title, children }) {
  return (
    <Paper variant='outlined' sx={{ borderColor: BORDER, borderRadius: 1.5, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.75, bgcolor: LIGHT_BLUE }}>
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: 1,
            bgcolor: BLUE,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { fontSize: 15 },
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ color: BLUE, fontWeight: 700, fontSize: 12, textTransform: "uppercase" }}>{title}</Typography>
      </Box>
      <Box sx={{ p: 1 }}>{children}</Box>
    </Paper>
  );
}

function KeyValueTable({ rows }) {
  return (
    <TableContainer sx={tableBox}>
      <Table size='small' sx={tableSx}>
        <TableBody>
          {rows.map(([label, value, bold]) => (
            <TableRow key={label}>
              <TableCell sx={{ width: "36%", borderRight: `1px solid ${BORDER}`, color: "#333", verticalAlign: "top" }}>{label}</TableCell>
              <TableCell sx={{ fontWeight: bold ? 700 : 400, whiteSpace: "pre-line" }}>{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function IconText({ icon, children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, "& svg": { fontSize: 13 } }}>
      {icon}
      <Typography sx={{ fontSize: 10.5 }}>{children}</Typography>
    </Box>
  );
}

function SignatureField({ label, value }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 10, color: "#555" }}>{label}</Typography>
      <Box sx={{ minHeight: 24, display: "flex", alignItems: "flex-end", borderBottom: `1.5px solid ${BLUE}` }}>{value && <Typography sx={{ fontSize: 11, fontWeight: 700, pb: 0.25 }}>{value}</Typography>}</Box>
    </Box>
  );
}

const ServiceWorkOrder = forwardRef(function ServiceWorkOrder({ reservation }, ref) {
  const { t, i18n } = useTranslation();
  const subtotal = reservation?.services?.reduce((total, service) => total + (service.price || 0), 0);
  const taxRate = reservation?.taxRate || 0.18;
  const tax = Math.round(subtotal * taxRate);
  const total = subtotal + tax;

  const customerDetails = useMemo(() => {
    if (!reservation) return [];
    const { customer } = reservation;
    return [
      [t("adminDashboard.name"), customer?.name],
      [t("adminDashboard.email"), customer?.email],
      [t("adminDashboard.phone"), customer?.phone],
    ];
  }, [t, reservation]);

  const vehicleDetails = useMemo(() => {
    if (!reservation) return [];
    const info = reservation?.car?.basicinfo?.basicinfo;
    const fuel2 = info?.FuelType2 && info.FuelType2 !== "-" ? ` · ${info.FuelType2}` : "";
    return [
      [t("adminDashboard.name"), info?.GenericName],
      [t("adminDashboard.plate"), reservation?.car?.plate],
      [t("adminDashboard.model"), info?.Model],
      [t("adminDashboard.bodyType"), info?.BodyType],
      [t("adminDashboard.dateOfRegister"), info?.DoR],
      [t("adminDashboard.fuelType"), `${info?.FuelType1 ?? ""}${fuel2}`],
    ];
  }, [t, reservation]);

  return (
    <ThemeProvider theme={theme}>
      <Paper
        elevation={0}
        ref={ref}
        sx={{
          width: "100%",
          maxWidth: MAX_WIDTH,
          mx: "auto",
          p: 1.5,
          bgcolor: "#f9fbfe",
          border: `1px solid ${BORDER}`,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
          <Box sx={{ position: "relative", width: 40, flexShrink: 0, color: BLUE }}>
            <DirectionsCarIcon sx={{ fontSize: 35 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: BLUE, fontWeight: 800, fontSize: 15, lineHeight: 1.15 }}>{reservation?.workshopDetails?.name}</Typography>
            <Typography sx={{ fontSize: 10.5, color: "#333", mb: 0.5 }}>{reservation?.workshopDetails?.tagline}</Typography>
            <IconText icon={<LocationOnIcon sx={{ color: BLUE }} />}>{reservation?.workshopDetails?.address}</IconText>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.25 }}>
              <IconText icon={<PhoneIcon sx={{ color: BLUE }} />}>{reservation?.workshopDetails?.phone[0]?.number}</IconText>
              <IconText icon={<EmailIcon sx={{ color: BLUE }} />}>{reservation?.workshopDetails?.email}</IconText>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: BORDER }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 1 }}>
          <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap",width: "100%"}}>
            <Typography sx={{ color: BLUE, fontWeight: 800, fontSize: 15, lineHeight: 1.1 }}>{t("workOrder.title")}</Typography>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                  alignSelf: "stretch",
                  borderRadius: 2,
                  p: 1,
                  backgroundColor: LIGHT_BLUE,
                }}
              >
                <Box sx={{ display: "flex", color: COLOR, flexShrink: 0 }}><CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} /></Box>
                <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", ...ellipsis }}>{t("workOrder.reservationDate")}</Typography>
                  <Typography sx={{ fontSize: 14, color: COLOR, fontWeight: 600, ...ellipsis }}>{dayjs(reservation?.date).format("D.M.YYYY")}</Typography>
                </Box>
              </Box>
          </Box>
        </Box>

        <Section icon={<PersonIcon />} title={t("workOrder.customerDetails")}>
          <KeyValueTable rows={customerDetails} />
        </Section>
        <Section icon={<DirectionsCarIcon />} title={t("workOrder.vehicleDetails")}>
          <KeyValueTable rows={vehicleDetails} />
        </Section>

        <Section icon={<BuildIcon />} title={t("workOrder.requestByUser")}>
          <Typography sx={{ fontSize: 12 }}>{reservation?.customer?.note}</Typography>
        </Section>

        <Section icon={<CalculateIcon />} title={t("workOrder.estimatedCost")}>
          <TableContainer sx={tableBox}>
            <Table size='small' sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCell, width: 40 }}>Sr.</TableCell>
                  <TableCell sx={{ ...headCell, width: "60%" }}>{t("workOrder.description")}</TableCell>
                  <TableCell sx={{ ...headCell, textAlign: "center" }}>{t("workOrder.quantity")}</TableCell>
                  <TableCell sx={{ ...headCell, textAlign: "right" }}>{t("workOrder.amount")} &euro;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservation?.services?.map((c, i) => (
                  <TableRow key={c.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{c.translations[i18n.language].name || c.translations["en"].name}</TableCell>
                    <TableCell align='center'>{c.qty || 1}</TableCell>
                    <TableCell align='right'>{c.price}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ ...tableBox, overflow: "hidden", mt: 1, ml: "auto", width: "62%", minWidth: 200 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", px: 1, py: 0.5 }}>
              <Typography sx={{ fontSize: 11 }}>{t("workOrder.subTotal")}</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 700 }}>&euro; {subtotal}</Typography>
            </Box>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between", px: 1, py: 0.5 }}>
              <Typography sx={{ fontSize: 11 }}>{t("workOrder.tax", { taxRate: taxRate * 100 })}</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 700 }}>&euro; {tax}</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 1,
                py: 0.75,
                bgcolor: BLUE,
                color: "#fff",
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{t("workOrder.total")}</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 800 }}>&euro; {total}</Typography>
            </Box>
          </Box>
        </Section>

        <Section icon={<EditNoteIcon />} title={t("workOrder.customerAuthorization")}>
          <Typography sx={{ fontSize: 10.5, color: "#333", lineHeight: 1.5, mb: 1.25 }}>{t("workOrder.declaration")}</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2 }}>
            <Box>
              <SignatureField label={t("workOrder.customerName")} value={customerDetails[0][1]} />
              <SignatureField label={t("workOrder.customerSign")} />
            </Box>
            <Box>
              <SignatureField label={t("workOrder.date")} />
              <SignatureField label={t("workOrder.workshopSign")} />
            </Box>
          </Box>
        </Section>
      </Paper>
    </ThemeProvider>
  );
});

export default ServiceWorkOrder;
